import { Alert, Box, Grid, TextField } from "@mui/material";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { ISaveSuccess, SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { ConfirmationDialogResult, TaskRepetitionType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as store from "../../../framework/customStore";
import { SaveDataObj } from "../../../framework/saveData";
import * as appReportService from "../../../models/services/appReportService";
import { ISessionState } from "../../../models/session/sessionState";
import * as storeActions from "../../../models/store/storeActions";
import { IModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { ITaskDataRow, SaveTaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { Base } from "ui/Scripts/source/framework/base";
import { IAppReportEditItem } from "ui/Scripts/source/models/appReport/appReportEditItem";
import { AppReportTemplateFormModel, getAppReportTemplateFromSchema, getSchemaFromAppReportTemplate, IAppReportTemplateFormModel } from "ui/Scripts/source/models/appReportTemplate/appReportTemplateFormModel";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { TreeCheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown";
import { DatePicker } from "source/components/framework/DateTime";
import { AppReportTemplateDialogFormDesigner } from "../AppReportTemplateDialog/AppReportTemplateDialogFormDesigner";
import { addComponentToTaskTypeCols, getComponentDefaultValueForNewIntance, getTaskFormDesignerComponentType, TaskFormDesignerComponentType } from "source/components/task/TaskTypeDialog/TaskTypeDialogFormDesigner/FormDesignerComponentPicker";
import { ITaskDataRowCol, TaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { ITaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { getUpdatedTaskDataRow, ITaskTypeFieldPropUpdate, updateDefaultValueToDataRowCols, updateTaskTypeColPropUpdate, updateValueToDataRowCol } from "source/components/task/TaskTypeDialog/FormDesignerTypes";
import { TaskTypeDialogSchemaDialog } from "source/components/task/TaskTypeDialog/TaskTypeDialogSchemaDialog";
import { TaskTypeDialogPreview } from "source/components/task/TaskTypeDialog/TaskTypeDialogPreview";
import { TaskTypeDialogPreviewType } from "source/components/task/TaskTypeDialog/TaskTypeDialogPreview/TaskTypeDialogPreviewWeb";
import { ModifyUserInfo } from "source/components/framework/User/ModifyUserInfo";
import { PublishButton } from "source/components/framework/Button/PublishButton";
import { IdColorTitle, IIdColorTitle } from "ui/Scripts/source/models/common/idColorTitle";
import { IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { getEventFiltersFromIdColorTitle, getEventFiltersFromNumberTitle, getEventIdTitleNodeFilters, getEventTypeFilters } from "source/components/event/EventPage/EventPageUtils";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { getTaskStates } from "source/components/task/TaskPage/TaskPageUtils";
import { IRecordCompanyFurtherEducationGroups } from "ui/Scripts/source/models/recordFurtherEducationGroup/recordCompanyFurtherEducationGroups";
import { RemoveToolButton } from "source/components/framework/Button/RemoveToolButton";

/* eslint-disable no-unused-vars */
export const enum AppReportEditDialogMenuItem {
    General = "1",
    Builder = "2",
    Preview = "3",
}
/* eslint-enable no-unused-vars */

interface IAppReportEditDialogModifyViewProp {
    activeMenuItem: AppReportEditDialogMenuItem;
    sessionState: ISessionState;
    companyId: string;
    appReport: IAppReportEditItem;
    canEdit: boolean;
    canEditWithoutLimit: boolean;
    canRemove: boolean;
    units: IIdTitleNode[];
    lockedUser: IModifyUser;
    isLockedToOtherUser: boolean;
    onAfterSaveToDb: (id: string) => void;
    onPrint: () => void;
}

interface IAppReportEditDialogModifyViewState {
    isLoading: boolean;
    id: string;
    rowId: string;
    unitId: string;
    code: string;
    comment: string;
    dataSchema: string;
    startDateStr: string;
    endDateStr: string;
    reportData: string;
    publicUrl: string;
    //AppReportTemplate
    formDataRow: ITaskDataRow;
    appReportTemplate: IAppReportTemplateFormModel;
    formSelectedField: string;
    showSchemaDesignerDialog: boolean;
    //EventReference related lists
    eventReports: IIdTitleNode[];
    eventUnits: IIdTitleNode[];
    eventStates: IIdTitle[];
    eventUrgencyClasses: IdColorTitle[];
    eventTypes: IIdTitleNode[];
    eventTypeBs: IIdTitleNode[];
    eventTypeCs: IIdTitleNode[];
    //TaskReference related lists
    taskTypes: ITaskEditTypeItem[];
    taskUnits: IIdTitleNode[];
    taskStates: IIdColorTitle[];
    //RecordReference related lists
    recordCharts: IIdTitle[];
    recordUnits: IIdTitleNode[];
    recordFurtherEducationGroups: IRecordCompanyFurtherEducationGroups[];
}

export type TAppReportEditDialogModifyViewRefProps = {
    validateBeforeSave: () => Promise<boolean>;
    saveToDb: () => Promise<SaveSuccess>;
    saveToDbIfChanged: () => Promise<SaveSuccess>;
    getOrgStateHash: () => string;
    setOrgStateHash: (newStateHash: string) => void;
    getCurrentStateHash: () => string;
};

export const AppReportEditDialogModifyView = forwardRef<TAppReportEditDialogModifyViewRefProps, IAppReportEditDialogModifyViewProp>((props, ref) => {
    const orgStateHash = useRef("");
    const [state, setState] = useState<IAppReportEditDialogModifyViewState>({
        isLoading: false,
        id: props.appReport.id,
        rowId: props.appReport.rowId,
        unitId: props.appReport.unitId,
        code: props.appReport.code,
        comment: props.appReport.comment,
        dataSchema: props.appReport.dataSchema,
        startDateStr: props.appReport.startDate
            ? Base.utcTimeToDateStr(props.appReport.startDate)
            : "",
        endDateStr: props.appReport.endDate
            ? Base.utcTimeToDateStr(props.appReport.endDate)
            : "",
        reportData: props.appReport.reportData,
        publicUrl: "",
        //AppReportTemplate
        formDataRow: null,
        appReportTemplate: null,
        formSelectedField: "",
        showSchemaDesignerDialog: false,
        //EventReference related lists
        eventReports: [],
        eventUnits: [],
        eventStates: [],
        eventUrgencyClasses: [],
        eventTypes: [],
        eventTypeBs: [],
        eventTypeCs: [],
        //TaskReference related lists
        taskTypes: [],
        taskUnits: [],
        taskStates: [],
        //RecordReference related lists
        recordCharts: [],
        recordUnits: [],
        recordFurtherEducationGroups: []
    });
    
    const checkErrors = (): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.NameMustBeDefined));
                return resolve(false);
            }
            if (!state.startDateStr || !state.endDateStr) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.IntervalMustBeDefined));
                return resolve(false);
            }
            return resolve(true);
        });
    };

    useEffect(() => {
        if (!props.appReport) return;
        const appReport = props.appReport;
        const parseResult = getAppReportTemplateFromSchema(appReport.dataSchema, state.appReportTemplate, state.formDataRow);
        AppReportTemplateFormModel.setTitlePageDataToFormDataRow(parseResult.appReportTemplate.formCols, parseResult.formDataRow, appReport.startDate, appReport.endDate);
        const newState = {
            ...state,
            id: appReport.id,
            rowId: appReport.rowId,
            unitId: appReport.unitId,
            code: appReport.code,
            comment: appReport.comment,
            dataSchema: appReport.dataSchema,
            startDateStr: appReport.startDate
                ? Base.utcTimeToDateStr(appReport.startDate)
                : "",
            endDateStr: appReport.endDate
                ? Base.utcTimeToDateStr(appReport.endDate)
                : "",
            reportData: appReport.reportData,
            publicUrl: appReport.publicUrl,
            //AppReportTemplate
            appReportTemplate: parseResult.appReportTemplate,
            formDataRow: parseResult.formDataRow,
        };
        setState((old) => ({
            ...old,
            ...newState
        }));
        const saveData = getSaveDataFromState(newState);
        orgStateHash.current = saveData.hash;
    }, [props.appReport]);

    useEffect(() => {
        if (!state.formDataRow || !state.appReportTemplate?.formCols) return;
        const formDataRow = new TaskDataRow(state.formDataRow);
        AppReportTemplateFormModel.setTitlePageDataToFormDataRow(state.appReportTemplate.formCols,
            formDataRow,
            state.startDateStr ? state.startDateStr.toUtcDate().getTime() : null,
            state.endDateStr ? state.endDateStr.toUtcDate().getTime() : null);
        setState(old => ({ ...old, formDataRow }));
    }, [state.startDateStr, state.endDateStr]);

    const loadEventReferenceRelatedLists = async (unitId: string) => {
        const result = await AppUtils.callService(() => appReportService.getAppReportEventReferenceData(props.companyId, unitId), false, false);
        if (!result) return;
        setState((old) => ({
            ...old,
            eventReports: result.eventReports,
            eventUnits: getEventIdTitleNodeFilters(result.eventUnits ? result.eventUnits : []),
            eventStates: getEventFiltersFromNumberTitle(result.eventStates ? result.eventStates : []),
            eventUrgencyClasses: getEventFiltersFromIdColorTitle(result.eventUrgencyClasses ? result.eventUrgencyClasses : []),
            eventTypes: getEventTypeFilters(result.eventTypes ? result.eventTypes : []),
            eventTypeBs: getEventTypeFilters(result.eventTypeBs ? result.eventTypeBs : []),
            eventTypeCs: getEventTypeFilters(result.eventTypeCs ? result.eventTypeCs : [])
        }));
    };

    const loadTaskReferenceRelatedLists = async (unitId: string) => {
        const result = await AppUtils.callService(() => appReportService.getAppReportTaskReferenceData(props.companyId, unitId), false, false);
        if (!result) return;
        setState((old) => ({
            ...old,
            taskTypes: result.taskTypes,
            taskUnits: getEventIdTitleNodeFilters(result.taskUnits ? result.taskUnits : []),
            taskStates: getTaskStates(result.taskStates ? result.taskStates : []),
        }));
    };

    const loadRecordReferenceRelatedLists = async (unitId: string) => {
        const result = await AppUtils.callService(() => appReportService.getAppReportRecordReferenceData(props.companyId, unitId), false, false);
        if (!result) return;
        setState((old) => ({
            ...old,
            recordCharts: result.recordCharts,
            recordUnits: getEventIdTitleNodeFilters(result.recordUnits ? result.recordUnits : []),
            recordFurtherEducationGroups: result.recordFurtherEducationGroups,
        }));
    };

    useEffect(() => {
        if (!state.unitId) return;
        loadEventReferenceRelatedLists(state.unitId);
        loadTaskReferenceRelatedLists(state.unitId);
        loadRecordReferenceRelatedLists(state.unitId);
    }, [state.unitId]);

    // #region Saving
    const getSaveDataFromState = (state: IAppReportEditDialogModifyViewState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("id", state.id);
        result.append("rowId", state.rowId);
        result.append("unitId", state.unitId);
        result.append("code", state.code);
        result.append("comment", state.comment);
        result.append("dataSchema", state.dataSchema);
        result.append("startDate", state.startDateStr ? state.startDateStr.toUtcDate().getTime() : null);
        result.append("endDate", state.endDateStr ? state.endDateStr.toUtcDate().getTime() : null);
        result.append("reportData", state.reportData);
        result.append("publicUrl", state.publicUrl);
        return result;
    };

    const validateBeforeSave = async (): Promise<boolean> => {
        return await AppUtils.validate(checkErrors);
    };

    const saveToDb = async (): Promise<SaveSuccess> => {
        if (!await validateBeforeSave()) return null;
        const saveData = getSaveDataFromState(state);
        //Call server
        const result = await AppUtils.callService(() => appReportService.saveAppReportEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        const stateChange = { id: result.id, rowId: result.rowId };
        orgStateHash.current = (getSaveDataFromState({ ...state, ...stateChange })).hash;
        setState((old) => ({
            ...old,
            ...stateChange
        }));
        return result;
    };

    const saveToDbIfChanged = async (): Promise<ISaveSuccess> => {
        if (getSaveDataFromState(state).hash === orgStateHash.current) {
            return { message: "", errorMessage: "", id: state.id, rowId: state.rowId };
        }
        return await saveToDb();
    };
    // #endregion Saving

    // #region Publish
    const getPublishData = (saveSuccess: SaveSuccess): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", saveSuccess.id);
        result.append("rowId", saveSuccess.rowId);
        result.append("companyId", props.companyId);
        result.append("dataSchema", state.dataSchema);
        result.append("taskDataRow", state.formDataRow
            ? JSON.stringify(SaveTaskDataRow.createSaveTaskDataRow(state.appReportTemplate.formCols, state.formDataRow, null))
            : null
        );
        return result;
    };

    const handlePublish = async () => {
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToPublishAppReportParameter, state.code), false) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const saveResult = await saveToDbIfChanged();
        if (!saveResult) return;
        const result = await AppUtils.callService(() => appReportService.publishAppReport(getPublishData(saveResult).obj));
        if (!result) return;
        props.onAfterSaveToDb(result.id);
    };

    const handleUnPublish = async () => {
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToUnpublishAppReportParameter, state.code), false) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const saveResult = await saveToDbIfChanged();
        if (!saveResult) return;
        const result = await AppUtils.callService(() => appReportService.unpublishAppReport(props.companyId, saveResult.id, saveResult.rowId));
        if (!result) return;
        props.onAfterSaveToDb(result.id);
    };
    // #endregion Publish

    // #region FormDesigner
    const handleFormSchemaReorder = (startIndex: number, endIndex: number) => {
        const result = Array.from(state.appReportTemplate.formCols);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        setState((old) => {
            const newAppReportTemplate = {
                ...old.appReportTemplate,
                formCols: result,
            };
            return {
                ...old,
                appReportTemplate: newAppReportTemplate,
                dataSchema: getSchemaFromAppReportTemplate(newAppReportTemplate)
            };
        });
    };

    const handleFormSchemaAddItem = (componentType: TaskFormDesignerComponentType, index: number, copyTaskTypeCol: ITaskTypeCol = null) => {
        setState((old) => {
            const result = addComponentToTaskTypeCols(old.appReportTemplate.formCols, componentType, index, copyTaskTypeCol);
            const dataRowCol = new TaskDataRowCol();
            dataRowCol.field = result.field;
            dataRowCol.valueStr = getComponentDefaultValueForNewIntance(componentType);
            const newAppReportTemplate = {
                ...old.appReportTemplate,
                formCols: [...result.typeCols],
            };
            const formDataRow = new TaskDataRow(state.formDataRow);
            formDataRow.cols = formDataRow.cols.filter(col => col.field !== result.field).concat([dataRowCol]);
            return {
                ...old,
                appReportTemplate: newAppReportTemplate,
                dataSchema: getSchemaFromAppReportTemplate(newAppReportTemplate),
                formDataRow,
                formSelectedField: result.field
            };
        });
    };

    const handleFormItemClick = (formSelectedField: string) => {
        setState((old) => ({
            ...old,
            formSelectedField
        }));
    };

    const handleFormTaskTypePropUpdate = (values: ITaskTypeFieldPropUpdate[]) => {
        //TypeCol update
        const updated = updateTaskTypeColPropUpdate(state.appReportTemplate.formCols, values);
        const appReportTemplate = {
            ...state.appReportTemplate,
            formCols: updated.taskTypeCols
        };
        //Default value update for data row
        const formDataRow = new TaskDataRow(state.formDataRow);
        if (updated.typeCol) {
            for (const value of values) {
                if (value.name === "defaultStr" || value.name === "defaultNbr") {
                    updateDefaultValueToDataRowCols(formDataRow, updated.typeCol, value.field);
                }
            }
        }
        AppReportTemplateFormModel.setTitlePageDataToFormDataRow(appReportTemplate.formCols,
            formDataRow,
            state.startDateStr ? state.startDateStr.toUtcDate().getTime() : null,
            state.endDateStr ? state.endDateStr.toUtcDate().getTime() : null);
        setState((old) => ({
            ...old,
            appReportTemplate,
            formDataRow,
            dataSchema: getSchemaFromAppReportTemplate(appReportTemplate)
        }));
    };

    const handleFormTaskDataRowValueUpdate = (field: string, name: string, value: string | number | boolean) => {
        if (!state.formDataRow) return;
        const formDataRow = new TaskDataRow(state.formDataRow);
        updateValueToDataRowCol(formDataRow, field, name, value);
        setState((old) => ({
            ...old,
            formDataRow
        }));
    };

    const handleFormRemoveComponent = async (field: string) => {
        if (await AppUtils.showConfirmationDialog(Translations.ReportTemplateRemoveFieldConfirmation, false) === ConfirmationDialogResult.No) {
            return;
        }
        const formDataRow = new TaskDataRow(state.formDataRow);
        formDataRow.cols = formDataRow.cols.filter(i => i.field !== field);
        setState((old) => {
            const newAppReportTemplate = {
                ...old.appReportTemplate,
                formCols: old.appReportTemplate?.formCols?.filter(item => item.field !== field)
            };
            return {
                ...old,
                appReportTemplate: newAppReportTemplate,
                dataSchema: getSchemaFromAppReportTemplate(newAppReportTemplate),
                formDataRow,
                formSelectedField: old.formSelectedField === field ? "" : old.formSelectedField
            };
        });
    };

    const handleFormCopyComponent = async (id: string) => {
        const taskTypeCol = state.appReportTemplate.formCols.find(i => i.field === id);
        if (!taskTypeCol) return;
        const designerComponentType = getTaskFormDesignerComponentType(taskTypeCol);
        handleFormSchemaAddItem(designerComponentType, state.appReportTemplate.formCols.findIndex(i => i.field === id) + 1, taskTypeCol);
    };

    const handleOpenSchemaDialog = () => {
        setState((old) => ({
            ...old,
            dataSchema: getSchemaFromAppReportTemplate(state.appReportTemplate),
            showSchemaDesignerDialog: true,
        }));
    };

    const handleSaveSchemaDialog = (value: string) => {
        const parseResult = getAppReportTemplateFromSchema(value, state.appReportTemplate, state.formDataRow);
        AppReportTemplateFormModel.setTitlePageDataToFormDataRow(parseResult.appReportTemplate.formCols, parseResult.formDataRow,
            state.startDateStr ? state.startDateStr.toUtcDate().getTime() : null,
            state.endDateStr ? state.endDateStr.toUtcDate().getTime() : null);
        setState((old) => ({
            ...old,
            appReportTemplate: parseResult.appReportTemplate,
            formDataRow: parseResult.formDataRow,
            dataSchemaParseError: parseResult.error,
            dataSchema: value,
            showSchemaDesignerDialog: false,
        }));
    };

    const handleCloseSchemaDialog = () => {
        setState((old) => ({ ...old, showSchemaDesignerDialog: false }));
    };
    // #endregion FormDesigner
    
    // #region Preview
    const handleUpdateFormDataRowCols = (taskDataRowCols: ITaskDataRowCol[]) => {
        const formDataRow = getUpdatedTaskDataRow(state.formDataRow, taskDataRowCols);
        setState((old) => ({
            ...old,
            formDataRow
        }));
    };
    // #endregion Preview
        
    useImperativeHandle(ref, () => {
        return {
            saveToDb,
            saveToDbIfChanged,
            validateBeforeSave,
            getOrgStateHash: () => orgStateHash.current,
            setOrgStateHash: (newStateHash: string) => { orgStateHash.current = newStateHash; },
            getCurrentStateHash: () => getSaveDataFromState(state).hash
        };
    }, [orgStateHash.current, state]);
        
    if (!props.appReport) return null;

    const isNew = props.appReport.isNew();
    const isPublished = !!props.appReport.publishUser;
    const isArchived = !!props.appReport.archiveUser;
    const isInTrash = !!props.appReport.deleteUser;
    const isReadOnly = !(props.canEdit && !isArchived);

    const canPublish = !isNew && !isArchived && !isInTrash && !props.isLockedToOtherUser && props.canEdit;
    const canUnPublish = !isNew && !isArchived && !isInTrash && isPublished && !props.isLockedToOtherUser && props.canEdit;
    const modifiedAfterPublish = isPublished && props.appReport.publishUser.date < props.appReport.modifyUser.date;

    return (
        <>
            {props.activeMenuItem === AppReportEditDialogMenuItem.General &&
                <Box height="100%">
                    <GridRow>
                        <Grid item sm={8}>
                            <GridRow>
                                <Grid item sm={2}>
                                    <FormField title={Translations.Unit}>
                                        <TreeCheckboxDropdown
                                            required={true}
                                            disabled={isReadOnly}
                                            singleSelect={true}
                                            selectedIds={[state.unitId]}
                                            showCommentInDropdown={true}
                                            values={props.units}
                                            onChange={(unitIds: string[]) => setState((old) => ({ ...old, unitId: unitIds[0] }))}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item sm={6}>
                                    <FormField title={Translations.Name} required>
                                        <TextField
                                            name="code"
                                            title={Translations.Name}
                                            inputProps={{ maxLength: 100 }}
                                            value={state.code}
                                            disabled={isReadOnly}
                                            required={true}
                                            onChange={(event) => setState((old) => ({ ...old, code: event.target.value }))}
                                            size="small"
                                            fullWidth
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item sm={4}>
                                    <FormField title={Translations.Interval} required>
                                        <GridRow>
                                            <Grid item sm={6}>
                                                <DatePicker
                                                    disabled={isReadOnly}
                                                    value={state.startDateStr}
                                                    onChange={(value: string) => setState((old) => ({ ...old, startDateStr: value }))}
                                                />
                                            </Grid>
                                            <Grid item sm={6}>
                                                <DatePicker
                                                    disabled={isReadOnly}
                                                    value={state.endDateStr}
                                                    minDate={state.startDateStr}
                                                    onChange={(value: string) => setState((old) => ({ ...old, endDateStr: value }))}
                                                />
                                            </Grid>
                                        </GridRow>
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={12}>
                                    <FormField title={Translations.Description}>
                                        <TextField
                                            value={state.comment}
                                            multiline
                                            inputProps={{ maxLength: 1000 }}
                                            minRows={5}
                                            disabled={isReadOnly}
                                            onChange={(event) => setState((old) => ({ ...old, comment: event.target.value }))}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                        </Grid>
                        <Grid item sm={4}>
                            <GridRow>
                                <Grid item sm={12}>
                                    <FormField title={Translations.TaskCreated} fullWidth margin={0}>
                                        <ModifyUserInfo
                                            modifyUser={props.appReport.createUser}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={12}>
                                    <FormField title={Translations.Modified} fullWidth>
                                        <ModifyUserInfo
                                            modifyUser={props.appReport.modifyUser}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                            {!isNew &&
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.Published} fullWidth>
                                            <ModifyUserInfo
                                                modifyUser={props.appReport.publishUser}
                                                suffixElement={canPublish || canUnPublish
                                                    ? <>
                                                        {canPublish &&
                                                            <PublishButton
                                                                published={isPublished}
                                                                modifiedAfterPublish={modifiedAfterPublish}
                                                                onClick={handlePublish}
                                                            />
                                                        }
                                                        {canUnPublish &&
                                                            <RemoveToolButton
                                                                tooltip={Translations.Unpublish}
                                                                onClick={handleUnPublish}
                                                            />
                                                        }
                                                    </>
                                                    : null
                                                }
                                            />
                                            {isPublished && modifiedAfterPublish &&
                                                <Alert severity="info" sx={{ marginTop: ".5rem" }}>
                                                    {Translations.ReportIsModifiedAfterItWasPublished}
                                                </Alert>
                                            }
                                        </FormField>
                                    </Grid>
                                </GridRow>
                            }
                            {!!props.appReport.archiveUser &&
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.Archived} fullWidth>
                                            <ModifyUserInfo
                                                modifyUser={props.appReport.archiveUser}
                                            />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                            }
                            {!!props.appReport.deleteUser &&
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.Removed} fullWidth>
                                            <ModifyUserInfo
                                                modifyUser={props.appReport.deleteUser}
                                            />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                            }
                        </Grid>
                    </GridRow>
                </Box>
            }
            {/* getAppReportEditDocumentFoldersAndDocuments */}
            {props.activeMenuItem === AppReportEditDialogMenuItem.Builder &&
                <AppReportTemplateDialogFormDesigner
                    companyId={props.companyId}
                    appReportTemplate={state.appReportTemplate}
                    formDataRow={state.formDataRow}
                    formTypeCol={state.appReportTemplate?.formCols?.find(item => item?.field === state.formSelectedField)}
                    sessionState={props.sessionState}
                    eventReports={state.eventReports}
                    eventUnits={state.eventUnits}
                    eventStates={state.eventStates}
                    eventUrgencyClasses={state.eventUrgencyClasses}
                    eventTypes={state.eventTypes}
                    eventTypeBs={state.eventTypeBs}
                    eventTypeCs={state.eventTypeCs}
                    taskTypes={state.taskTypes}
                    taskUnits={state.taskUnits}
                    taskStates={state.taskStates}
                    recordCharts={state.recordCharts}
                    recordUnits={state.recordUnits}
                    recordFurtherEducationGroups={state.recordFurtherEducationGroups}
                    onFormSchemaReorder={handleFormSchemaReorder}
                    onFormSchemaAddItem={handleFormSchemaAddItem}
                    onFormItemClick={handleFormItemClick}
                    onTaskTypeFormPropUpdate={handleFormTaskTypePropUpdate}
                    onFormTaskDataRowValueUpdate={handleFormTaskDataRowValueUpdate}
                    onFormRemoveComponent={handleFormRemoveComponent}
                    onFormCopyComponent={handleFormCopyComponent}
                    onViewSchema={handleOpenSchemaDialog}
                    onGetDocumentFoldersAndDocuments={() => appReportService.getAppReportEditDocumentFoldersAndDocuments(props.companyId, state.unitId)}
                />
            }
            {props.activeMenuItem === AppReportEditDialogMenuItem.Preview &&
                <TaskTypeDialogPreview
                    appReportDesignMode={true}
                    previewType={TaskTypeDialogPreviewType.Print}
                    taskRepetitionType={TaskRepetitionType.NotRepeated}
                    dataSchema={state.dataSchema}
                    headerDataRow={null}
                    formDataRow={state.formDataRow}
                    lineDataRow={null}
                    //Print preview
                    companyId={props.companyId}
                    code={state.code}
                    printOrientationLandscape={false}
                    //Properties Panel
                    headerTypeCol={null}
                    formTypeCol={state.appReportTemplate?.formCols?.find(item => item?.field === state.formSelectedField)}
                    lineTypeCol={null}
                    lineColumnWidths={[]}
                    onHeaderItemClick={null}
                    onFormItemClick={handleFormItemClick}
                    onTaskTypeHeaderPropUpdate={null}
                    onTaskTypeFormPropUpdate={handleFormTaskTypePropUpdate}
                    onUpdateHeaderDataRowCols={null}
                    onUpdateFormDataRowCols={handleUpdateFormDataRowCols}
                    onChangeLineColumnWidth={null}
                />
            }
            {state.showSchemaDesignerDialog && (
                <TaskTypeDialogSchemaDialog
                    dataSchema={state.dataSchema}
                    onSave={handleSaveSchemaDialog}
                    onClose={handleCloseSchemaDialog}
                />
            )}
        </>
    );
});

AppReportEditDialogModifyView.displayName = "AppReportEditDialogModifyView";